import { AgentContext, ApiAgentContext, ApiUrlContext } from '../../types';
import { ChatMessage } from './types';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function buildUrlContext(
  location: { pathname: string; search: string } = window.location,
): ApiUrlContext {
  return {
    uri: location.pathname,
    query_params: Object.fromEntries(new URLSearchParams(location.search)),
  };
}

export function toApiContext(context?: AgentContext): ApiAgentContext {
  const url_context = buildUrlContext();
  if (!context) {
    return { url_context };
  }
  return {
    environment_ids: context.environmentIds,
    incident_ids: context.incidentIds,
    asset_ids: context.assetIds,
    test_ids: context.testIds,
    url_context,
  };
}

export function parseContextFromContent(content: string): AgentContext {
  const context: AgentContext = {};
  for (const line of content.split('\n')) {
    const eqIdx = line.indexOf('=');
    if (eqIdx === -1) continue;
    const key = line.slice(0, eqIdx);
    const value = line.slice(eqIdx + 1);
    if (!value) continue;
    if (key === 'incident_id') {
      context.incidentIds = [...(context.incidentIds ?? []), value];
    } else if (key === 'asset_id') {
      context.assetIds = [...(context.assetIds ?? []), value];
    } else if (key === 'test_id') {
      context.testIds = [...(context.testIds ?? []), value];
    } else if (key === 'env_id') {
      context.environmentIds = [...(context.environmentIds ?? []), value];
    }
  }
  return context;
}

export function processHistoryMessages(messages: ChatMessage[]): ChatMessage[] {
  const result: ChatMessage[] = [];
  let pendingContext: AgentContext | null = null;

  for (const msg of messages) {
    if (msg.hidden === true) {
      const parsed = parseContextFromContent(msg.content ?? '');
      pendingContext = pendingContext
        ? {
            incidentIds: [
              ...(pendingContext.incidentIds ?? []),
              ...(parsed.incidentIds ?? []),
            ],
            assetIds: [
              ...(pendingContext.assetIds ?? []),
              ...(parsed.assetIds ?? []),
            ],
            testIds: [
              ...(pendingContext.testIds ?? []),
              ...(parsed.testIds ?? []),
            ],
            environmentIds: [
              ...(pendingContext.environmentIds ?? []),
              ...(parsed.environmentIds ?? []),
            ],
          }
        : parsed;
      continue;
    }

    if (msg.role === 'user' && pendingContext) {
      result.push({ ...msg, context: pendingContext });
      pendingContext = null;
    } else {
      result.push(msg);
    }
  }

  return result;
}

const escapeCSVValue = (val: any): string => {
  const str = val == null ? '' : String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const downloadTableAsCSV = (
  rows: Record<string, any>[],
  columns: string[],
) => {
  const header = columns.map(escapeCSVValue).join(',');
  const body = rows.map((row) =>
    columns.map((col) => escapeCSVValue(row[col])).join(','),
  );
  const csv = [header, ...body].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'table.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
